import { Menu } from "@mantine/core";
import { GraphQL, isGraphQLError } from "@rpi/openapi-api";
import { IconV3, useToasts } from "@rpi/openapi-core";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { RpaTableMenu } from "../../../components/RpaTableMenu";
import { Body } from "../../../components/Typography";
import { ConfirmationModalV2 } from "../../../components/modals/ConfirmationModalV2";
import { apiKeyStatusBadgeConfig, isEnabled, nextStatus } from "./apiKeyStatus";

interface EnableDisableActionProps {
  id: number;
  status: GraphQL.ApiKeyStatus;
}

export const EnableDisableAction: React.FC<EnableDisableActionProps> = ({ id, status }) => {
  const { showSuccessToast, showErrorToast } = useToasts();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>();

  const target = nextStatus(status);
  const label = actionLabel(status);
  const enabled = isEnabled(status);

  const { mutate, isLoading } = GraphQL.useSetApiCredentialStatusMutation<unknown>({
    onSuccess: async () => {
      showSuccessToast(`API Key ${apiKeyStatusBadgeConfig[target].label.toLowerCase()}`);
      await queryClient.invalidateQueries({ queryKey: ["ApiCredentials"] });
      setConfirmOpen(false);
      setError(undefined);
    },
    onError: (err) => {
      const message = isGraphQLError(err) ? err.message : "Failed to update API key status";
      setError(message);
      showErrorToast(message);
    },
  });

  const openConfirm = () => {
    setError(undefined);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    if (isLoading) return;
    setConfirmOpen(false);
    setError(undefined);
  };

  const handleConfirm = () => mutate({ id, status: target });

  return (
    <>
      <RpaTableMenu>
        <Menu.Item
          icon={<IconV3 icon="key" size={20} color="inherit" />}
          data-testid="enable-disable-action"
          data-intent-danger={enabled || undefined}
          onClick={openConfirm}
        >
          {label} API Key
        </Menu.Item>
      </RpaTableMenu>

      <ConfirmationModalV2
        opened={confirmOpen}
        onClose={closeConfirm}
        onConfirm={handleConfirm}
        isLoading={isLoading}
        error={error}
        title={`${label} API Key?`}
        message={<ConfirmBody status={status} />}
        confirmLabel={`${label} Key`}
        cancelLabel="Cancel"
        confirmIntent={enabled ? "danger" : "primary"}
        loadingLabel={`${progressLabel(status)} Key…`}
      />
    </>
  );
};

function actionLabel(status: GraphQL.ApiKeyStatus): string {
  return isEnabled(status) ? "Disable" : "Enable";
}

function progressLabel(status: GraphQL.ApiKeyStatus): string {
  return isEnabled(status) ? "Disabling" : "Enabling";
}

const ConfirmBody: React.FC<{ status: GraphQL.ApiKeyStatus }> = ({ status }) =>
  isEnabled(status) ? (
    <Body>Any application using this key will stop working immediately. You can re-enable it anytime.</Body>
  ) : (
    <Body>Applications using this key will regain access immediately.</Body>
  );
